import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { isNativeApp, isIOS } from "@/lib/nativeBridge";
import useIsUsAccount from "@/hooks/useIsUsAccount";
import { CheckCircleIcon as CheckCircle, EnvelopeSimpleIcon as EnvelopeSimple, SparkleIcon as Sparkle, XIcon as CloseIcon } from "@phosphor-icons/react";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import BuyerConsentGate, { useBuyerConsent } from "@/components/BuyerConsentGate";
import IapUpgradeButton from "@/components/IapUpgradeButton";


/**
 * Infer the lead-source tag for an inquiry based on the current path
 * + referrer. Stays a small enum the backend whitelists (everything
 * else gets normalised to "other" server-side).
 */
function inferLeadSource() {
  try {
    const p = (window.location.pathname || "").toLowerCase();
    if (p.startsWith("/k/")) return "kennel_page";
    if (p.startsWith("/marketplace")) return "marketplace";
    if (p.startsWith("/breeds/")) return "breed_catalog";
    if (p.startsWith("/search")) return "search";
    if (p.startsWith("/d/")) return "share_card";
    const ref = (document.referrer || "").toLowerCase();
    if (ref.includes("/k/")) return "kennel_page";
    if (ref.includes("/marketplace")) return "marketplace";
    if (ref.includes("/breeds/")) return "breed_catalog";
    if (ref.includes("instagram") || ref.includes("facebook") || ref.includes("twitter") || ref.includes("t.co")) {
      return "share_card";
    }
    return "direct";
  } catch {
    return "other";
  }
}

/**
 * "Show interest" button used on marketplace listing cards and on the
 * dog detail page. Works for any logged-in viewer who isn't the owner.
 *
 *   Free tier:        3 inquiries / calendar month → 4th hits paywall.
 *   Buyer Pass / any breeder plan / admin: unlimited.
 *
 * Props:
 *   dog       — the dog row (must include id, name, created_by, for_sale)
 *   currentUserId — the logged-in viewer's id (so we can hide for owners)
 *   variant   — visual variant: "primary" (full-width on detail page) or
 *               "card" (compact button used inside listing cards).
 *   testIdSuffix — appended to data-testid for disambiguation.
 */
export default function InquireButton({
  dog,
  currentUserId,
  variant = "primary",
  testIdSuffix = "",
}) {
  const isUsAccount = useIsUsAccount();
  const [inquired, setInquired] = useState(false);
  const [busy, setBusy] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const consent = useBuyerConsent(dog);

  // Hide for the owner / unlisted dogs / unauth.
  const isOwner = currentUserId && currentUserId === dog?.created_by;
  const visible = !!currentUserId && !isOwner && !!dog?.for_sale;

  // Fetch inquiry status once.
  useEffect(() => {
    if (!visible || !dog?.id) return;
    let cancelled = false;
    api
      .get(`/marketplace/listings/${dog.id}/inquiry-status`)
      .then((r) => {
        if (!cancelled) setInquired(!!r.data?.inquired);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [visible, dog?.id]);

  if (!visible) return null;

  const onClick = async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (inquired) return;
    if (!consent.ok) {
      toast.info(consent.isRestricted
        ? "Acknowledge the restricted-breed notice below to inquire."
        : "Please confirm the buyer checklist below to inquire.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/marketplace/listings/${dog.id}/inquire`, {
        buyer_consent: { acknowledged: true, breed_warning: dog?.breed_warning || null },
        source: inferLeadSource(),
      });
      if (data.already_inquired) {
        setInquired(true);
        toast.info("You've already inquired about this dog.");
        return;
      }
      setInquired(true);
      track("inquiry_submitted", {
        listing_id: dog.id,
        breed: dog.breed || null,
        kennel_id: dog?.breeder?.id || null,
      });
      toast.success(
        `Interest sent — the breeder has been notified by email and will contact you directly${
          data.remaining >= 0
            ? ` · ${data.remaining} free ${
                data.remaining === 1 ? "inquiry" : "inquiries"
              } left this month`
            : ""
        }.`
      );
    } catch (err) {
      // 402 → upgrade paywall
      if (err.response?.status === 402) {
        setPaywallOpen(true);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const baseClasses =
    variant === "primary"
      ? `w-full rounded-full text-sm font-semibold ${
          inquired
            ? "bg-[#0F8043] hover:bg-[#0F8043] text-white"
            : "bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
        }`
      : `rounded-full text-xs h-8 ${
          inquired
            ? "bg-[#F0F8F1] text-[#0F8043] border border-[#0F8043]/40"
            : "bg-white text-[#0E0E0E] border border-[#0E0E0E] hover:bg-[#0E0E0E] hover:text-white"
        }`;

  return (
    <>
      <Button
        type="button"
        size={variant === "primary" ? "default" : "sm"}
        onClick={onClick}
        disabled={busy || inquired || !consent.ok}
        className={baseClasses}
        data-testid={`inquire-btn${testIdSuffix ? `-${testIdSuffix}` : ""}`}
      >
        {inquired ? (
          <>
            <CheckCircle size={14} weight="duotone" className="mr-1.5" />
            Interest sent
          </>
        ) : (
          <>
            <EnvelopeSimple size={14} weight="duotone" className="mr-1.5" />
            {busy ? "Sending…" : variant === "primary" ? "Show interest" : "Inquire"}
          </>
        )}
      </Button>
      {!inquired && (
        <BuyerConsentGate
          listing={dog}
          actionLabel="inquire"
          testId={`consent-gate-inquire-${dog.id}`}
          onConsentChange={consent.refresh}
        />
      )}

      {/* Paywall — buyer hit the 3/month free-tier limit */}
      <Dialog open={paywallOpen} onOpenChange={setPaywallOpen}>
        <DialogContent className="sm:max-w-md" data-testid="inquiry-paywall">
          <DialogHeader>
            <DialogTitle className="font-spectral text-2xl flex items-center gap-2">
              <Sparkle size={18} weight="duotone" className="text-gold" />
              You've used your free inquiries
            </DialogTitle>
            <DialogDescription>
              {isNativeApp() ? (
                <>
                  Free accounts can send 3 inquiries per month. An active
                  buyer plan unlocks <strong>unlimited</strong> inquiries
                  plus watchlists, saved searches, and breed-match alerts.
                </>
              ) : (
                <>
                  Free accounts can send 3 inquiries per month. Buyer Pass
                  unlocks <strong>unlimited</strong> inquiries plus
                  watchlists, saved searches, and breed-match alerts — for
                  $4.99/month.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <ul className="text-sm text-[#3F3F3F] space-y-2 mt-2">
            <li className="flex items-start gap-2">
              <CheckCircle size={14} weight="duotone" className="text-[#0F8043] mt-0.5 flex-shrink-0" />
              Unlimited breeder inquiries
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={14} weight="duotone" className="text-[#0F8043] mt-0.5 flex-shrink-0" />
              Watchlists + saved searches
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={14} weight="duotone" className="text-[#0F8043] mt-0.5 flex-shrink-0" />
              Email alerts when matching litters drop
            </li>
          </ul>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setPaywallOpen(false)}
              className="rounded-full"
            >
              Maybe later
            </Button>
            {isNativeApp() ? (
              isIOS() ? (
                <IapUpgradeButton
                  packageIds={["buyer_pass_monthly", "buyer_pass_annual"]}
                  label="Upgrade to Buyer Pass"
                  context="inquiry_limit"
                  className="w-full sm:w-auto"
                />
              ) : isUsAccount ? (
                <Button asChild className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white">
                  <a
                    href="https://armstrongk9registry.com/billing"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="inquiry-paywall-upgrade"
                  >
                    <Sparkle size={13} weight="duotone" className="mr-1" />
                    Continue on our website
                  </a>
                </Button>
              ) : null
            ) : (
              <Button asChild className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white">
                <Link to="/billing" data-testid="inquiry-paywall-upgrade">
                  <Sparkle size={13} weight="duotone" className="mr-1" />
                  Upgrade to Buyer Pass
                </Link>
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
